import {effect, EventEmitter, inject, Injectable, Signal} from '@angular/core';
import {LocationHttpService} from "../../../../http-requests/location-http.service";
import {CompositionTypeHttpService} from "../../../../http-requests/composition-type-http.service";
import {EmploymentRelationshipHttpService} from "../../../company/x-http-requests/employment-relationship-http.service";
import {SourceDetailsHttpService} from "../../../../http-requests/source-details-http.service";
import {EventHttpService} from "../../../../management/x-http-requests/event-http.service";
import {ActivatedRoute} from "@angular/router";
import {ErrorCodes} from "../../common/enums/states/error-codes";

import {LocationDto} from "../../../../models/location-dto";
import {CompositionTypeNl} from "../../../../models/composition-type/composition-type-nl";
import {EventDetailsDto} from "../../../../models/event/event-details";
import {EmployeeCostDto} from "../../../../models/employee-cost-dto";
import {SourceDetailDto} from "../../../../models/source-detail/source-detail-dto";
import {forkJoin} from "rxjs";
import {TimeRepository} from "../repositories/core/time-repository/time-repository";
import {SalesRepository} from "../repositories/core/sales-repository/sales-repository";
import {LinkedDtoRepository} from "../../common/repositories/linked-dto-repository";
import {PaymentMethodTranslation} from "../../../../mappings/payment-method-translation";
import {SalesUtils} from "../utils/sales/sales-utils";
import {ColorMapService} from "../../common/colors/color-map.service";
import {EmploymentCostsRepository} from "../repositories/core/employment-costs/employment-costs-repository";
import {RevenueCalculationStrategy} from "../../common/enums/strategies/revenue-calculation-strategy";
import {SalesDto, SalesDtoKeys} from "../models/sales/sales-dto";
import {FilterVO} from "../../common/utils/filter/models/filter-vo";
import {GlobalAlertService} from "../../../../_services/global-alert.service";
import {UrlHelper} from "../../../../helpers/url-helper";
import {CompositionVariationCost} from "../../common/models/costs/composition-variation-cost";
import {
  CompositionGroupingMode,
  CompositionGroupingPreferenceService
} from "../../../../_shared/composition-grouping";
import {FilterKeys} from "../../common/utils/filter/enums/filter-keys";


interface IEventAnalysisStoreService {
  revenueCalculationMethodOption: RevenueCalculationStrategy;
  initializedNotifier: EventEmitter<any>
  updateNotifier: EventEmitter<any>
  groupingModeNotifier: EventEmitter<CompositionGroupingMode>

  update(): void;

  getEventName(): string;

  getTotalRevenue(): number;

  getTotalTaxes(): number;

  getTotalSourceDetailsCost(): number;

  getTotalEmployeeCosts(): number;
}

@Injectable()
export class EventAnalysisStoreService implements IEventAnalysisStoreService {
  activeEventId: string = ''
  eventName: string = ''
  analysisErrorState: ErrorCodes | undefined
  isInitialized: boolean = false

  initializedNotifier: EventEmitter<any> = new EventEmitter<any>()
  updateNotifier: EventEmitter<any> = new EventEmitter<any>()
  errorNotifier: EventEmitter<ErrorCodes> = new EventEmitter<ErrorCodes>()
  /**
   * #534: fires BEFORE {@link updateNotifier} whenever "Kombinationen einzeln" flips. Stores whose row TEMPLATE
   * depends on the grouping key (the composition-overview table builds one `Row` per key) rebuild on this;
   * stores that only aggregate (top sales) need nothing beyond the `updateNotifier` that follows.
   */
  groupingModeNotifier: EventEmitter<CompositionGroupingMode> = new EventEmitter<CompositionGroupingMode>()

  private readonly groupingPreference = inject(CompositionGroupingPreferenceService);

  /** The shared "Kombinationen einzeln" preference (one app-wide instance, also driving the event-details page). */
  readonly groupingMode: Signal<CompositionGroupingMode> = this.groupingPreference.mode;

  /** The mode the current rows/filters were built for — the effect below compares against it, so its first
   * (synchronous, mount-time) run is a no-op rather than a spurious rebuild. */
  private appliedGroupingMode: CompositionGroupingMode = this.groupingPreference.mode();

  revenueCalculationMethodOption: RevenueCalculationStrategy = RevenueCalculationStrategy.revenue;

  revenueCalculationMethod: { [id in RevenueCalculationStrategy]: (salesDto: SalesDto) => number } = {
    [RevenueCalculationStrategy.revenue]: (salesDto: SalesDto) => SalesUtils.getRevenue(salesDto),
    [RevenueCalculationStrategy.revenueExcludingTaxes]: (salesDto: SalesDto) => SalesUtils.getRevenueAfterTax(salesDto),
    [RevenueCalculationStrategy.revenueExcludingSourceDetails]: (salesDto: SalesDto) => SalesUtils.getRevenueExcludingSourceDetails(salesDto),
    [RevenueCalculationStrategy.revenueExcludingTaxesAndSourceDetails]: (salesDto: SalesDto) => SalesUtils.getRevenueExcludingTaxesAndSourceDetails(salesDto),
  }

  isNetRevenue: boolean = false;
  isSourceDetailsCostExcluded: boolean = false;
  revenueByAttributes: { name: string, revenue: number }[] = []
  currentAttributeOption: keyof SalesDto = SalesDtoKeys.locationId

  constructor(
    private locationHttpService: LocationHttpService,
    private compositionTypeHttpService: CompositionTypeHttpService,
    private employmentRelationshipHttpService: EmploymentRelationshipHttpService,
    private sourceDetailsHttpService: SourceDetailsHttpService,
    private eventHttpService: EventHttpService,
    private activatedRoute: ActivatedRoute,
    private colorMapService: ColorMapService,
    public timeRepository: TimeRepository,
    private salesRepository: SalesRepository,
    private linkedDtoRepository: LinkedDtoRepository<any>,
    private employmentCostRepository: EmploymentCostsRepository,
    private globalAlertService: GlobalAlertService
  ) {
    // React to the shared toggle. An effect (not a subscription) because the preference IS a signal; the
    // appliedGroupingMode guard makes the mandatory first run inert.
    effect(() => {
      let mode = this.groupingMode();
      if (mode === this.appliedGroupingMode) {
        return;
      }
      this.applyGroupingMode(mode);
    });

    setTimeout(() => {
      this.changeErrorState(ErrorCodes.loading)
      this.assignActiveEventIdFromRoute()

      if (!this.verifyEventId()) {
        return
      }
      this.fetchInitialData()
    }, 0)

  }

  assignActiveEventIdFromRoute() {
    this.activeEventId = UrlHelper.QueryParams.eventId;
    // this.activeEventId = this.activatedRoute.snapshot.queryParams.eventId;
  }

  verifyEventId(): boolean {
    if (this.activeEventId === "" || this.activeEventId === undefined) {
      this.changeErrorState(ErrorCodes.invalidEventId)
      return false
    }
    return true
  }

  changeErrorState(errorCode: ErrorCodes | undefined) {
    this.analysisErrorState = errorCode
    this.errorNotifier.emit(errorCode)
  }

  fetchInitialData() {
    let sources = [
      this.locationHttpService.list(),
      this.compositionTypeHttpService.ListNl(),
      this.employmentRelationshipHttpService.getEmployeeCostsFromEvent(this.activeEventId),
      this.sourceDetailsHttpService.getSourceDetailsOfEvent(this.activeEventId),
      this.eventHttpService.getExtendedEvent(this.activeEventId, ""),
    ]

    forkJoin(sources).subscribe(([locations, compositions, employmentRelationships, sourceDetails, event]) => {
        if (!CompositionTypeNl.containsDefault(compositions as CompositionTypeNl[])) {
          (compositions as CompositionTypeNl[]).push(CompositionTypeNl.default())
        }
      let sourceDetailsAsDto = SalesUtils.sourceDetailsToModelList(sourceDetails as CompositionVariationCost[])

        this.initialize(locations as LocationDto[], compositions as CompositionTypeNl[], event as EventDetailsDto, employmentRelationships as EmployeeCostDto[], sourceDetailsAsDto as SourceDetailDto[])

      }, (error) => {
        this.changeErrorState(ErrorCodes.serverError)
        this.globalAlertService.createAndShow("Fehler beim Laden der Daten", "Es ist ein Fehler beim Laden der Daten aufgetreten. Bitte versuchen Sie es später erneut.")
      }
    )//.unsubscribe()
  }

  initialize(locationDto: LocationDto[], compositionTypeNl: CompositionTypeNl[], eventHttpDto: EventDetailsDto, employeeDto: EmployeeCostDto[], sourceDetails: SourceDetailDto[]): void {
    this.timeRepository.initialize(eventHttpDto.start, eventHttpDto.end)
    this.eventName = eventHttpDto.name

    this.salesRepository.initialize(eventHttpDto)

    this.initializeLinkedDtoRepository(locationDto, compositionTypeNl, sourceDetails);
    this.salesRepository.postInitialize();

    this.colorMapService.synchronizeDtoDataUtilsWithColorMapping(this.linkedDtoRepository)

    this.employmentCostRepository.initialize(employeeDto)

    this.initializedNotifier.emit()
    this.isInitialized = true;

    this.update()
  }

  update() {

    this.salesRepository.update(this.revenueCalculationMethod[this.revenueCalculationMethodOption])
    this.updateRevenueByAttribute()

    if (this.isInitialized) {
      this.changeErrorState(ErrorCodes.loading)
      this.updateNotifier.emit()
      this.changeErrorState(undefined)

    }
  }

  /**
   * The `SalesDto` property rows are grouped by in the current mode — `compositionId` (combined) or
   * `compositionVariantKey` (individual). Deliberately the ONE place the branch lives, so the table, its
   * filters and the top-sales ranking can never disagree about what a row is.
   */
  compositionRowKey(): SalesDtoKeys.compositionId | SalesDtoKeys.compositionVariantKey {
    return this.groupingMode() === 'individual' ? SalesDtoKeys.compositionVariantKey : SalesDtoKeys.compositionId;
  }

  /** The filter key matching {@link compositionRowKey} — what a row checkbox adds/removes. */
  compositionRowFilterKey(): FilterKeys {
    return this.groupingMode() === 'individual' ? FilterKeys.CompositionVariantKey : FilterKeys.CompositionId;
  }

  /** {@link compositionRowKey} as an accessor, for the table/top-sales bucketing loops. */
  compositionRowKeyFn(): (salesDto: SalesDto) => string {
    let key = this.compositionRowKey();
    return (salesDto: SalesDto) => salesDto[key];
  }

  /**
   * Applies a flipped grouping mode: drop EVERY composition-row filter of either kind, then let the stores
   * rebuild and re-aggregate.
   *
   * Clearing both kinds is the point. A `CompositionId` filter left standing while individual rows are drawn
   * (or the reverse) keeps hiding that revenue with no checkbox anywhere to un-hide it — the numbers would
   * just be quietly wrong. Filtered-out sales are held in the cache wrapper and merged back by the next
   * `FilterHelper.applyFilter`, which `update()` triggers.
   */
  private applyGroupingMode(mode: CompositionGroupingMode) {
    this.appliedGroupingMode = mode;
    this.clearCompositionFilters();

    if (!this.isInitialized) {
      return;
    }

    this.groupingModeNotifier.emit(mode);
    this.update();
  }

  /** Removes every active `CompositionId`/`CompositionVariantKey` filter without triggering an update. */
  private clearCompositionFilters() {
    let compositionFilters = this.salesRepository.filterHelper.filter
      .filter(filter => filter.key === FilterKeys.CompositionId || filter.key === FilterKeys.CompositionVariantKey);
    for (let filter of compositionFilters) {
      this.removeFilterWithoutUpdate(filter);
    }
  }

  getEventName(): string {
    return this.eventName;
  }

  getTotalRevenue(): number {
    return this.salesRepository.getTotalRevenue();
  }

  getTotalTaxes(): number {
    return this.salesRepository.getTotalTaxes();
  }

  getTotalSourceDetailsCost(): number {
    return this.salesRepository.getTotalSourceDetailsCost();
  }

  getTotalEmployeeCosts(): number {
    return this.employmentCostRepository.getTotalEmployeeCosts();
  }

  getTotalEmployeeHours(): number {
    return this.employmentCostRepository.getTotalEmployeeHours();
  }

  getRevenueCalculation(): (salesDto: SalesDto) => number {
    return this.revenueCalculationMethod[this.revenueCalculationMethodOption]
  }

  setCurrentAttributeOption(option: keyof SalesDto) {
    this.currentAttributeOption = option
    this.updateRevenueByAttribute()
  }

  updateRevenueByAttribute() {
    this.revenueByAttributes = []
    if (this.currentAttributeOption === undefined) {
      console.debug("skipping", this.currentAttributeOption)
      return;
    }
    for (let entry of this.salesRepository.getRevenueByAttribute(this.currentAttributeOption)) {
      this.revenueByAttributes.push({
        name: this.linkedDtoRepository.getName(entry.id, this.currentAttributeOption),
        revenue: entry.value
      })
    }
  }

  getSoldArticleCount(): number {
    return this.salesRepository.getSoldArticleCount();
  }

  setRevenueCalculationMethodOption(isNetRevenue: boolean, isSourceDetailsCostExcluded: boolean) {
    if (this.isNetRevenue === isNetRevenue && this.isSourceDetailsCostExcluded === isSourceDetailsCostExcluded) {
      return;
    }
    this.isNetRevenue = isNetRevenue;
    this.isSourceDetailsCostExcluded = isSourceDetailsCostExcluded;
    if (this.isNetRevenue && this.isSourceDetailsCostExcluded) {
      this.revenueCalculationMethodOption = RevenueCalculationStrategy.revenueExcludingTaxesAndSourceDetails;
    } else if (this.isNetRevenue) {
      this.revenueCalculationMethodOption = RevenueCalculationStrategy.revenueExcludingTaxes;
    } else if (this.isSourceDetailsCostExcluded) {
      this.revenueCalculationMethodOption = RevenueCalculationStrategy.revenueExcludingSourceDetails;
    } else {
      this.revenueCalculationMethodOption = RevenueCalculationStrategy.revenue;
    }
    this.update()
  }

  addFilterWithoutUpdate<T>(filter: FilterVO<T>) {
    this.salesRepository.addFilter(filter)
  }

  removeFilterWithoutUpdate<T>(filter: FilterVO<T>) {
    this.salesRepository.removeFilter(filter)
  }

  addFilter<T>(filter: FilterVO<T>) {
    this.addFilterWithoutUpdate(filter);
    this.update()
  }

  removeFilter<T>(filter: FilterVO<T>) {
    this.removeFilterWithoutUpdate(filter);
    this.update();
  }

  swapZeroRevenueSet(direction: boolean) {
    this.salesRepository.swapZeroRevenueSet(direction)
    this.update()
  }

  private initializeLinkedDtoRepository(locationDto: LocationDto[], compositionTypeNl: CompositionTypeNl[], sourceDetails: SourceDetailDto[]) {
    this.linkedDtoRepository.setDtoData("locationId", locationDto)
    this.linkedDtoRepository.setDtoData("compositionTypeId", compositionTypeNl)
    this.linkedDtoRepository.setDtoDataFromDictionary("compositionId", SalesUtils.getCompositionIdMapping(this.salesRepository.getSalesWrapper()))
    // #534: built unconditionally beside the composition-id mapping, so flipping "Kombinationen einzeln" is a
    // pure re-render (no refetch, no re-derivation).
    this.linkedDtoRepository.setDtoDataFromDictionary("compositionVariantKey", SalesUtils.getCompositionVariantKeyMapping(this.salesRepository.getSalesWrapper()))
    this.linkedDtoRepository.setDtoDataFromDictionary("bookingType", PaymentMethodTranslation.paymentMethodToModelDict())

    this.linkedDtoRepository.setDtoData("sourceDetailsCost", sourceDetails)
  }

}
